import { useMemo, useState } from 'react'
import { CheckCircle, XCircle, BarChart2 } from 'lucide-react'
import AppLayout from '../AppLayout'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { REFERENDUM_COMUNI, type ComuneReferendum } from '../../data/referendum'
import type { AmbitoId } from '../../data/ambitiStats'

export default function ReferendumPage() {
  return (
    <AppLayout>
      {({ selectedAmbito, onSelectedAmbitoChange }) => (
        <ReferendumContent
          selectedAmbito={selectedAmbito}
          onSelectedAmbitoChange={onSelectedAmbitoChange}
        />
      )}
    </AppLayout>
  )
}

type SortKey = 'nome' | 'percentuale'
type SortDir = 'asc' | 'desc'
type Filter = 'TUTTI' | 'SI' | 'NO'

function ReferendumContent({
  selectedAmbito,
  onSelectedAmbitoChange,
}: {
  selectedAmbito: AmbitoId | null
  onSelectedAmbitoChange: (id: AmbitoId | null) => void
}) {
  const [filter, setFilter] = useState<Filter>('TUTTI')
  const [sortKey, setSortKey] = useState<SortKey>('nome')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const filtered = useMemo(
    () => selectedAmbito
      ? REFERENDUM_COMUNI.filter(c => c.ambitoId === selectedAmbito)
      : REFERENDUM_COMUNI,
    [selectedAmbito],
  )

  const totSi = filtered.filter(c => c.voto === 'SI').length
  const totNo = filtered.filter(c => c.voto === 'NO').length
  const totale = filtered.length

  const avgSi = useMemo(() => {
    const si = filtered.filter(c => c.voto === 'SI')
    return si.length ? si.reduce((s, c) => s + c.percentuale, 0) / si.length : 0
  }, [filtered])

  const avgNo = useMemo(() => {
    const no = filtered.filter(c => c.voto === 'NO')
    return no.length ? no.reduce((s, c) => s + c.percentuale, 0) / no.length : 0
  }, [filtered])

  const maxSi = useMemo(() => {
    const si = filtered.filter(c => c.voto === 'SI')
    return si.length ? si.reduce((max, c) => c.percentuale > max.percentuale ? c : max) : null
  }, [filtered])

  const maxNo = useMemo(() => {
    const no = filtered.filter(c => c.voto === 'NO')
    return no.length ? no.reduce((max, c) => c.percentuale > max.percentuale ? c : max) : null
  }, [filtered])

  const quasiSi = useMemo(() =>
    [...filtered.filter(c => c.voto === 'SI')]
      .sort((a, b) => a.percentuale - b.percentuale)
      .slice(0, 5),
    [filtered],
  )

  const quasiNo = useMemo(() =>
    [...filtered.filter(c => c.voto === 'NO')]
      .sort((a, b) => a.percentuale - b.percentuale)
      .slice(0, 5),
    [filtered],
  )

  const topSi = useMemo(() =>
    [...filtered.filter(c => c.voto === 'SI')]
      .sort((a, b) => b.percentuale - a.percentuale)
      .slice(0, 5),
    [filtered],
  )

  const topNo = useMemo(() =>
    [...filtered.filter(c => c.voto === 'NO')]
      .sort((a, b) => b.percentuale - a.percentuale)
      .slice(0, 5),
    [filtered],
  )

  const sorted = useMemo(() => {
    const list = filter === 'TUTTI'
      ? [...filtered]
      : filtered.filter(c => c.voto === filter)
    return list.sort((a, b) => {
      const v = sortKey === 'nome'
        ? a.nome.localeCompare(b.nome, 'it')
        : a.percentuale - b.percentuale
      return sortDir === 'asc' ? v : -v
    })
  }, [filter, sortKey, sortDir, filtered])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  const titolo = selectedAmbito
    ? `Ambito di ${selectedAmbito.charAt(0).toUpperCase() + selectedAmbito.slice(1)} — Referendum Costituzionale 2026`
    : 'Referendum Costituzionale 2026 — Risultati in Provincia di Monza e della Brianza'

  return (
    <section className="telemetry-dashboard">
      <div className="telemetry-inner">
        <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>
          {titolo}
        </h3>

        {/* Riga 1: pannello, mappa, SÌ, NO, quasi-SÌ */}
        <AutoGrid>
          <BSDCard kind="panel" sezione="Referendum Costituzionale 2026" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <p style={{ color: 'var(--text-1)', fontSize: 12, lineHeight: 1.7, margin: 0 }}>
              Il <strong>referendum costituzionale</strong> del 22 e 23 marzo 2026 ha coinvolto <strong>{totale} comuni</strong> della Brianza{selectedAmbito ? " nell'ambito selezionato" : ""}.
              <br />
              <br />
              Ha prevalso il SÌ in <strong style={{ color: '#10b981' }}>{totSi} comuni</strong> ({totale ? Math.round((totSi / totale) * 100) : 0}%)
              e il NO in <strong style={{ color: '#ef4444' }}>{totNo} comuni</strong> ({totale ? Math.round((totNo / totale) * 100) : 0}%).
              <br /><br />
              
            </p>
          </BSDCard>

          {/* Mappa ambiti */}
          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            style={{ minHeight: 120, padding: 10 }}
          >
            <DistrictMap
              onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
              onClearSelection={() => onSelectedAmbitoChange(null)}
              selectedDistrictId={selectedAmbito}
            />
          </BSDCard>

          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Comuni con prevalenza SÌ"
            value={String(totSi)}
            icon={CheckCircle}
            color="#10b981"
            sub={
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                Media SÌ: <strong style={{ color: '#10b981' }}>{avgSi.toFixed(1)}%</strong>
                <br />
                {maxSi && <>Massimo: <strong>{maxSi.nome}</strong> ({maxSi.percentuale}%)</>}
              </p>
            }
          />

          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Comuni con prevalenza NO"
            value={String(totNo)}
            icon={XCircle}
            color="#ef4444"
            sub={
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                Media NO: <strong style={{ color: '#ef4444' }}>{avgNo.toFixed(1)}%</strong>
                <br />
                {maxNo && <>Massimo: <strong>{maxNo.nome}</strong> ({maxNo.percentuale}%)</>}
              </p>
            }
          />

          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Per poco SÌ"
            value={quasiSi[0] ? `${quasiSi[0].percentuale.toFixed(2)}%` : '—'}
            icon={CheckCircle}
            color="#f59e0b"
            sub={
              <div style={{ display: 'grid', gap: 5 }}>
                <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '0 0 4px' }}>
                  Comuni con il margine SÌ più risicato:
                </p>
                {quasiSi.map((c: ComuneReferendum) => (
                  <div key={c.nome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', flexShrink: 0 }}>+{(c.percentuale - 50).toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            }
          />
        </AutoGrid>

        {/* Riga 2: quasi-NO, top-SÌ, top-NO, grafico, tabella */}
        <AutoGrid style={{ marginTop: 14 }}>
          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Per poco NO"
            value={quasiNo[0] ? `${quasiNo[0].percentuale.toFixed(2)}%` : '—'}
            icon={XCircle}
            color="#f59e0b"
            sub={
              <div style={{ display: 'grid', gap: 5 }}>
                <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '0 0 4px' }}>
                  Comuni con il margine NO più risicato:
                </p>
                {quasiNo.map((c: ComuneReferendum) => (
                  <div key={c.nome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', flexShrink: 0 }}>+{(c.percentuale - 50).toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            }
          />

          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Top 5 SÌ — maggior margine"
            value={topSi[0] ? `${topSi[0].percentuale.toFixed(2)}%` : '—'}
            icon={CheckCircle}
            color="#10b981"
            sub={
              <div style={{ display: 'grid', gap: 5 }}>
                <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '0 0 4px' }}>
                  Comuni con il margine SÌ più ampio:
                </p>
                {topSi.map((c: ComuneReferendum) => (
                  <div key={c.nome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', flexShrink: 0 }}>+{(c.percentuale - 50).toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            }
          />

          <BSDCard
            kind="kpi"
            sezione="Referendum Costituzionale 2026"
            label="Top 5 NO — maggior margine"
            value={topNo[0] ? `${topNo[0].percentuale.toFixed(2)}%` : '—'}
            icon={XCircle}
            color="#ef4444"
            sub={
              <div style={{ display: 'grid', gap: 5 }}>
                <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '0 0 4px' }}>
                  Comuni con il margine NO più ampio:
                </p>
                {topNo.map((c: ComuneReferendum) => (
                  <div key={c.nome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', flexShrink: 0 }}>+{(c.percentuale - 50).toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            }
          />

          {/* Grafico */}
          <BSDCard
            kind="panel"
            sezione="Referendum Costituzionale 2026"
            zoomable
            style={{ display: 'flex', flexDirection: 'column', maxHeight: 380 }}
          >
            <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <BarChart2 size={14} style={{ color: 'var(--accent)' }} />
              Distribuzione per comune
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, overflowY: 'auto', flex: 1, maxHeight: 'min(100%, 280px)' }}>
              {[...filtered]
                .sort((a, b) => b.percentuale - a.percentuale)
                .map(c => (
                  <div key={c.nome} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-2)', width: 160, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.nome}
                    </span>
                    <div style={{ flex: 1, height: 14, background: 'var(--bg-2)', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                      <div style={{ height: '100%', width: `${c.percentuale}%`, background: c.voto === 'SI' ? '#10b981' : '#ef4444', borderRadius: 3, transition: 'width 0.4s' }} />
                      <div style={{ position: 'absolute', left: 6, top: 0, height: '100%', display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>
                        {c.voto}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: c.voto === 'SI' ? '#10b981' : '#ef4444', width: 58, flexShrink: 0, textAlign: 'right' }}>
                      {c.percentuale.toFixed(2)}%
                    </span>
                  </div>
                ))}
            </div>
          </BSDCard>

          {/* Tabella comuni */}
          <BSDCard
            kind="panel"
            sezione="Referendum Costituzionale 2026"
            zoomable
            style={{ display: 'flex', flexDirection: 'column', maxHeight: 380 }}
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <h3 className="telemetry-section-title" style={{ margin: 0, marginRight: 8 }}>Comune per comune</h3>
              {(['TUTTI', 'SI', 'NO'] as Filter[]).map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  style={{
                    padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 12, border: '1px solid', cursor: 'pointer',
                    borderColor: filter === f ? (f === 'SI' ? '#10b981' : f === 'NO' ? '#ef4444' : 'var(--accent)') : 'var(--border)',
                    background: filter === f ? (f === 'SI' ? '#10b98120' : f === 'NO' ? '#ef444420' : 'var(--accent)20') : 'transparent',
                    color: filter === f ? (f === 'SI' ? '#10b981' : f === 'NO' ? '#ef4444' : 'var(--accent)') : 'var(--text-3)',
                  }}
                >
                  {f === 'TUTTI' ? 'Tutti' : f === 'SI' ? 'SÌ' : 'NO'}
                </button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>{sorted.length} comuni</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 60px', gap: 8, padding: '4px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
              {([['nome', 'Comune'], ['percentuale', '%']] as [SortKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSort(key)}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, fontWeight: 700, color: sortKey === key ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 3 }}
                >
                  {label}{sortKey === key && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </button>
              ))}
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)' }}>Esito</span>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, maxHeight: 'min(100%, 280px)' }}>
              {sorted.map((c, i) => (
                <ComuneRow key={c.nome} comune={c} even={i % 2 === 0} />
              ))}
            </div>
          </BSDCard>
        </AutoGrid>
      </div>
    </section>
  )
}

function ComuneRow({ comune, even }: { comune: ComuneReferendum; even: boolean }) {
  const isSi = comune.voto === 'SI'
  const color = isSi ? '#10b981' : '#ef4444'
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 60px', gap: 8, padding: '6px 8px', borderRadius: 4, background: even ? 'transparent' : 'var(--bg-1)', alignItems: 'start' }}>
      <span style={{ fontSize: 12, color: 'var(--text-1)', wordBreak: 'break-word', lineHeight: 1.4 }}>{comune.nome}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color }}>{comune.percentuale.toFixed(2)}%</span>
      <span style={{ fontSize: 11, fontWeight: 700, color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {isSi
          ? <CheckCircle size={12} style={{ color: '#10b981' }} />
          : <XCircle size={12} style={{ color: '#ef4444' }} />}
        {isSi ? 'SÌ' : 'NO'}
      </span>
    </div>
  )
}
